import { Box, useToast } from '@chakra-ui/react'
import { ModalBuild } from 'components/molecules'
import { Bar } from 'components/molecules/Bar'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { TableBuildActionsType } from 'components/organisms/TableBuild/type'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import {
    UserOrderBy,
    UsersNewResponseDTO,
    UsersQueryDTO,
    UsersResponseDTO
} from 'models'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { changeUserStatus } from 'services/users'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import FormUser from './components/FormUser'
import { loader } from './constants'

type UserStatus = 'ATIVO' | 'BLOQUEADO'

const Users: React.FC = () => {
    const [loading, setLoading] = useState(true)

    const toast = useToast()
    const [filter, setFilter] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const [totalPages, setTotalPages] = useState(1)
    const [dataSingleUser, setDataSingleUser] = useState<UsersNewResponseDTO>()

    const {
        userPerfis,
        currentInstitution,
        loadingCreateUser,
        getInstitutionUsers,
        getInstitutionsPerfis,
        getInstitutionUser
    } = useInstitutionsContext()
    const lastInstitutionRef = useRef<string | undefined>(undefined)
    const [userIdForm, setIdUserForm] = useState<string>('')
    const [usersIF, setUsersIF] = useState<UsersResponseDTO[]>()

    const { isBackoffice } = useAuthContext()

    const addModalRef = useRef<ModalBuildHandle>(null)

    const [orderBy, setOrderBy] = useState<
        Required<Pick<UsersQueryDTO, 'ordenacao' | 'tipoOrdenacao'>>
    >({
        ordenacao: 'nome',
        tipoOrdenacao: 'ASC'
    })

    const [table, setTable] = useState<
        TableBuildProps<
            | 'nome'
            | 'tipoUsuarioPerfil'
            | 'email'
            | 'cpf'
            | 'alocacao'
            | 'utilizaMfa'
        >
    >({
        header: [
            {
                name: 'nome',
                text: 'Nome',
                order: true,
                position: 0
            },
            {
                name: 'tipoUsuarioPerfil',
                text: 'Acesso',
                position: 1
            },
            {
                name: 'email',
                text: 'E-mail',
                position: 2
            },
            {
                name: 'cpf',
                text: 'CPF',
                position: 3
            },
            {
                name: 'alocacao',
                text: 'Unidade de Alocação',
                position: 4
            },
            {
                name: 'utilizaMfa',
                text: 'Utiliza MFA',
                position: 5
            }
        ],
        action: [
            {
                type: 'SWITCH'
            },
            {
                type: 'EDIT'
            }
        ]
    })
    const buildTable = (users: UsersResponseDTO[]) => {
        return users.map(
            ({ cpf, tipoUsuarioPerfil, utilizaMfa, ...values }) => ({
                cpf: formatter.cpf(cpf),
                tipoUsuarioPerfil: tipoUsuarioPerfil?.perfil,
                utilizaMfa: utilizaMfa ? 'Sim' : 'Não',
                ...values
            })
        )
    }

    const handlePage = (value: number) => {
        if (page !== value) {
            setPage(value)
            setLoading(true)
        }
    }

    const handleLimit = (value: number) => {
        setLimit(value)
        setLoading(true)
    }

    const handleAction = (
        type: TableBuildActionsType,
        id: number | string,
        value: string | number | boolean | undefined
    ) => {
        switch (type) {
            case 'EDIT':
                handleEdit(String(id))
                break
            case 'SWITCH':
                handleDisable(String(id), value)
                break
        }
    }

    const handleOrderBy = (ordenacao: UserOrderBy) => {
        setOrderBy(old => ({
            ordenacao,
            tipoOrdenacao:
                old.ordenacao !== ordenacao
                    ? old.tipoOrdenacao
                    : old.tipoOrdenacao === 'ASC'
                    ? 'DESC'
                    : 'ASC'
        }))
        setLoading(true)
    }

    const handleAdd = (): void => {
        setIdUserForm('')
        addModalRef.current?.onOpen()
    }

    const handleCancel = (): void => {
        setIdUserForm('')
        addModalRef.current?.onClose()
    }

    const handleEdit = async (idUser: string): Promise<void> => {
        setIdUserForm(idUser)
        if (!currentInstitution || !idUser) {
            return
        }

        try {
            const { data } = await getInstitutionUser(
                currentInstitution.id,
                idUser,
                isBackoffice
            )

            setDataSingleUser(data)
            addModalRef.current?.onOpen()
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu um erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            handleCancel()
        }
    }

    const handleDisable = async (
        id: string,
        value: string | number | boolean | undefined
    ): Promise<void> => {
        try {
            const status: UserStatus = value ? 'ATIVO' : 'BLOQUEADO'
            await changeUserStatus(id, { statusUsuario: status })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        }
    }

    const handleSearch = useDebounce((e: ChangeEvent<HTMLInputElement>) => {
        setLoading(true)
        const value = e.target.value.toLowerCase()
        setFilter(value)
        setPage(1)
    })

    const handleSubmit = () => {
        fetchGetUsers()
        handleCancel()
    }

    const fetchGetUsers = async (query?: UsersQueryDTO): Promise<void> => {
        if (!currentInstitution?.id) {
            return
        }

        try {
            setLoading(true)

            const queryParams = {
                ...query,
                ...orderBy,
                pagina: page,
                limite: limit
            }

            const { data, headers } = await getInstitutionUsers(
                currentInstitution.id,
                queryParams
            )

            setUsersIF(data)
            setTable(prevTable => ({
                ...prevTable,
                body: buildTable(data)
            }))

            setTotalPages(+headers['total-pages'])
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (currentInstitution?.id === undefined) {
            setLoading(false)
            setTable(prev => ({
                ...prev,
                body: []
            }))
            return
        }

        const institutionChanged =
            lastInstitutionRef.current !== currentInstitution.id

        if (institutionChanged) {
            lastInstitutionRef.current = currentInstitution.id
            getInstitutionsPerfis()

            if (page !== 1) {
                setPage(1)
                return
            }
        }

        fetchGetUsers({
            nome: filter ?? ''
        })
    }, [filter, page, limit, loadingCreateUser, currentInstitution?.id])

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {currentInstitution && (
                <Bar
                    title="Usuários"
                    input={{
                        props: {
                            w: '100%',
                            variant: 'flushed',
                            placeholder: 'Buscar pelo nome'
                        }
                    }}
                    action={{
                        text: 'Novo usuário',
                        props: {
                            colorScheme: 'primary'
                        }
                    }}
                    onClickActions={handleAdd}
                    onChangeInput={handleSearch}
                />
            )}
            <TableBuild
                header={table.header}
                body={table.body}
                action={table.action}
                ordenation={orderBy}
                error={{
                    action: {
                        text: filter ? 'search' : 'user',
                        custom: currentInstitution
                            ? undefined
                            : 'Selecione uma Instituição Financeira',
                        onClick: () =>
                            currentInstitution && !filter && handleAdd()
                    }
                }}
                loading={{
                    status: loading,
                    loader: loader
                }}
                pagination={{
                    page,
                    limit,
                    totalPages
                }}
                onOrderBy={order =>
                    handleOrderBy(order as unknown as UserOrderBy)
                }
                onAction={handleAction}
                onChangePage={handlePage}
                onChangeLimit={handleLimit}
            ></TableBuild>

            <ModalBuild
                ref={addModalRef}
                closeOnOverlayClick={false}
                title=" "
                size="2xl"
            >
                <FormUser
                    data={dataSingleUser}
                    idUser={userIdForm}
                    perfis={userPerfis}
                    onSubmitForm={handleSubmit}
                    onCancelForm={handleCancel}
                />
            </ModalBuild>
        </Box>
    )
}

export default Users
